import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import ProductForm from 'components/Forms/ProductForm'
import TextInput from 'components/Inputs/TextInput'
import Select from 'components/Inputs/Select'
import MainButton from 'components/Buttons/MainButton'
import businessProductValidator from 'validators/businessProductValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'

import {
  Container,
  Content,
  Row,
  InfoText
} from './styles'

export default function BusinessProductsModal ({
  visible,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [loading, setLoading] = useState (true)
  const [products, setProducts] = useState ([])
  const [categories, setCategories] = useState ([])
  const [category, setCategory] = useState (null)
  const [name, setName] = useState ('')
  const [orderBy, setOrderBy] = useState ('')
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [category, name])

  const getData = async () => {
    try { 
      setLoading (true)

      const { data: categories } = await cutileiApi.get ('/product_categories', requestConfig)

      setCategories (categories.map (category => ({
        label: category.name,
        value: category.id
      })))

      await getProducts ()
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const getProducts = async () => {
    let filters = `business_id=${businessId}`
    if (name) filters += `&name=${encodeURIComponent (name)}`
    if (category) filters += `&category_id=${category.value}`
    if (orderBy) filters += `&order_by=${orderBy}`

    try {
      setLoading (true)

      const { data: products } = await cutileiApi.get (
      `/products?${filters}`, requestConfig
      )

      setProducts (products)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='xl' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Adicionar produtos</DialogTitle>
        </DialogHeader>
        {/* Os filtros ficam parados entre o título e a lista, que rola */}
        <Row style={{marginBottom: 15, marginInline: 20}}>
          <Select
            name='category'
            placeholder='Categoria...'
            value={category}
            options={categories}
            isClearable={true}
            onChange={(_, option) => setCategory (option)}
            containerStyles={{
              width: 280
            }}
          />
          <TextInput
            value={name}
            placeholder='Pesquisar por nome'
            isClearable={true}
            onChange={e => setName (e.target.value)}
            onClear={() => setName ('')}
            containerStyle={{width: 280}}
            inputStyle={{boxShadow: '0px 1px 10px -6px', paddingInline: 12}}
          />
        </Row>
        {loading ? (
          <DialogBody><Loading/></DialogBody>
        ) : (
          <Formik
            validationSchema={businessProductValidator}
            initialValues={{
              'products': products.map (product => ({
                'id': product.id,
                'selected': false,
                'name': product.name,
                'nickname': '',
                'total_stock': 1,
                'total_content': 1,
                'content_unit': {label: 'Unidade (un)', value: 'un'},
                'use_in_service': null,
                'cost': 0,
                'price': 0,
                'comission_percentage': 20
              }))
            }}
            onSubmit={async (values, { setSubmitting }) => {
              const products = values.products.filter (product => product.selected).map (product => ({
                ...product,
                use_in_service: product.use_in_service?.value,
                available_stock: product.total_stock,
                available_content: product.total_content,
                content_unit: product.content_unit.value,
                comission_percentage: product.comission_percentage / 100,
              }))

              try {
                await cutileiApi.post (`/businesses/${businessId}/products`, {products}, requestConfig)
              
                setSubmitting (false)
                handleConfirm (products)
                handleClose ()
              } catch (error) {
                setSubmitting (false)
                logApiError (error)
              }
            }}
          >
            {(formikProps) => (
              <>
                <DialogBody>
                  <Container>
                    <Content>
                      {products.length === 0 ? (
                        <InfoText>
                          Nenhum resultado encontrado
                        </InfoText>
                      ) : products.map ((product, index) => (
                        <ProductForm 
                          key={product.id}
                          index={index}
                          formikProps={formikProps}
                          data={product}
                        />
                      ))}
                    </Content>
                  </Container>
                </DialogBody>
                <DialogFooter>
                  <Row>
                    <MainButton
                      label='Confirmar'
                      onClick={formikProps.handleSubmit}
                      disabled={formikProps.isSubmitting}
                      loading={formikProps.isSubmitting}
                      width={140}
                      style={{marginTop: 5}}
                    />
                  </Row>
                </DialogFooter>
              </>
            )}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
